import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const SITE_NAME = 'The Cosmic Finder App'
const SITE_ORIGIN = 'https://app.thecosmicfinder.com'
const DEFAULT_DESCRIPTION =
  "The Cosmic Finder is an oracle card reading app personalized by your chart, the current sky, and the readings you've had before. Daily card pulls, astrological weather, moon phases, live ceremonies, and personal sessions with creators on the platform."

interface PageMeta {
  title: string
  description: string
}

const PAGE_META: Record<string, PageMeta> = {
  // '/' is what crawlers see (the prerendered signed-out landing page), so
  // its title leads with the exact search phrase — "oracle card reading
  // app" — the same keyword-first format the pillar pages use.
  '/': {
    title: 'Oracle Card Reading App',
    description:
      'An oracle card reading app from Reno, Nevada — daily pulls personalized by your chart, plus astrological weather, moon phases, and live ceremonies.',
  },
  '/find-direction': {
    title: 'Find Direction',
    description:
      "Today's sky, planetary aspects, and moon phases — read through your natal pattern. Astrological weather translated into plain language.",
  },
  '/find-clarity': {
    title: 'Find Clarity',
    description:
      "Draw an oracle card and receive a personalized reading shaped by your chart, your intention, and the deck's lineage.",
  },
  '/find-events': {
    title: 'Find Events',
    description:
      'Live oracle ceremonies, group readings, and events with creators on The Cosmic Finder platform. RSVP for upcoming live sessions.',
  },
  '/find-practitioners': {
    title: 'Find Creators',
    description:
      "Meet the creators on The Cosmic Finder platform. Book personal sessions in astrology, Akashic record healing, breathwork, and more — each session billed directly with the creator.",
  },
  '/practitioners': {
    title: 'Creator',
    description:
      "A creator on The Cosmic Finder platform — bio, offerings, and booking link.",
  },
  '/find-community': {
    title: 'Find Community',
    description:
      'Share readings, reflections, and moments with The Cosmic Finder community.',
  },
  '/profile': { title: 'Profile', description: DEFAULT_DESCRIPTION },
  '/moon-calendar': {
    title: 'Moon Calendar',
    description:
      'Full moon phases, lunar cycles, and meanings. Track the moon with The Cosmic Finder App.',
  },
  '/journal': {
    title: 'Journal',
    description:
      'Reflect on your oracle readings and capture what the cards stirred in you.',
  },
  '/admin': { title: 'Admin', description: DEFAULT_DESCRIPTION },
  '/reading': {
    title: 'Your Reading',
    description:
      'Receive a personalized oracle reading with rich interpretation, astrology, and elemental correspondences.',
  },
  '/onboarding': {
    title: 'Welcome',
    description: 'Begin your cosmic journey with The Cosmic Finder App.',
  },
  '/services': {
    title: 'Personal sessions',
    description:
      "Book a personal session with one of our creators. Astrology, Akashic record healing, intuitive health coaching, breathwork, and more — each session billed directly with the creator.",
  },
  '/sign-in': {
    title: 'Sign In',
    description: 'Sign in to your Cosmic Finder altar.',
  },
  '/sign-up': {
    title: 'Sign Up',
    description: 'Create your free Cosmic Finder account and begin your cosmic journey.',
  },
  '/terms': {
    title: 'Terms & Privacy',
    description:
      'Terms of service and privacy policy for The Cosmic Finder App — how readings are stored, how the app uses AI, and how to contact us.',
  },
  '/install': {
    title: 'Install the App',
    description:
      'Add The Cosmic Finder to your home screen for one-tap access to your daily card, the sky right now, and ceremony reminders.',
  },
  // SEO pillar pages — kept distinct so each one ranks for its own query.
  // Title format leads with the keyword phrase before the brand.
  // Descriptions kept under ~155 chars so they don't truncate in SERP
  // snippets. The first sentence is the SERP-visible portion.
  '/oracle-reading-app': {
    title: 'What is an Oracle Reading App?',
    description:
      "An oracle reading app lets you draw cards from an oracle deck with personalized interpretations shaped by your chart and your story. See what makes The Cosmic Finder different.",
  },
  '/astrological-weather': {
    title: 'Astrological Weather — Today\'s Sky',
    description:
      "Astrological weather is the day's sky in plain language — moon phase, signs, aspects, retrogrades. The Cosmic Finder gives you the daily forecast, free.",
  },
  '/pull-a-card': {
    title: 'How to Pull an Oracle Card',
    description:
      'A short ritual for pulling a single oracle card with intention — settle, set an intention, draw, read, sit with it. The Cosmic Finder makes it a daily practice.',
  },
  '/oracle-card-reading-reno': {
    title: 'Oracle Card Reading in Reno, Nevada',
    description:
      'Oracle card reading in Reno, Nevada with Sarah McCallum, an intuitive with twenty years of experience. Try the daily app free or book a personal session.',
  },
  '/find-your-signs': {
    title: 'Find Your Signs — Launch Event at Wyld Market',
    description:
      'Friday May 15, 6–8 PM at Wyld Market in Reno. Zodiac stations, oracle pulls, La Condesa libations — the in-person launch of The Cosmic Finder app with Sarah McCallum.',
  },
}

function setMeta(keyAttr: 'name' | 'property', keyValue: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${keyAttr}="${keyValue}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(keyAttr, keyValue)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

export function useDocumentTitle() {
  const { pathname } = useLocation()

  useEffect(() => {
    const key = Object.keys(PAGE_META).find((k) =>
      k === '/' ? pathname === '/' : pathname.startsWith(k)
    )
    const meta = key ? PAGE_META[key] : null
    const pageTitle = meta?.title ?? null
    const description = meta?.description ?? DEFAULT_DESCRIPTION
    const fullTitle = pageTitle ? `${pageTitle} - ${SITE_NAME}` : SITE_NAME
    // Netlify's pretty_urls processing canonicalizes nested routes with a
    // trailing slash. Match that in the canonical link so we're consistent
    // regardless of how the visitor arrived.
    const normalizedPath =
      pathname === '/' || pathname.endsWith('/') ? pathname : `${pathname}/`
    const canonical = `${SITE_ORIGIN}${normalizedPath}`

    document.title = fullTitle
    setMeta('name', 'description', description)
    setMeta('property', 'og:title', fullTitle)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', canonical)
    setMeta('name', 'twitter:title', fullTitle)
    setMeta('name', 'twitter:description', description)
    setCanonical(canonical)
  }, [pathname])
}
